function blockKey(x, y, z) {
  return `${Math.round(x)},${Math.round(y)},${Math.round(z)}`;
}

function parseBlockKey(key) {
  const [x, y, z] = String(key).split(',').map(Number);
  return { x, y, z };
}

export function createFarmingSystem({
  world,
  ui,
  itemDefs,
  spawnItemDrop,
  onUseHoe = null
}) {
  const FARMLAND_ID = 49;
  const CROP_STAGE_MIN = 50;
  const CROP_STAGE_MAX = 53;
  const SAPLING_ID = 65;
  const CROPS = new Set([50, 51, 52, 53]);
  const TILLABLE = new Set([1, 2]);
  const WATER_ID = 7;
  const HYDRATION_RADIUS = 4;
  const HYDRATION_VERTICAL_RANGE = 1;
  const HYDRATION_CACHE_TTL = 1.25;

  const cropStates = new Map();
  const farmlandStates = new Map();
  const hydrationCache = new Map();

  let cropTickTimer = 0;
  let farmlandTickTimer = 0;
  let hydrationClock = 0;

  function isCropBlockId(id) {
    return CROPS.has(Number(id));
  }

  function getGrowthThreshold(stageId) {
    if (stageId <= 50) return 8.5;
    if (stageId === 51) return 10.5;
    return 12.5;
  }

  function registerFarmland(x, y, z) {
    farmlandStates.set(blockKey(x, y, z), { dryTime: 0 });
  }

  function registerCrop(x, y, z) {
    const key = blockKey(x, y, z);
    if (!cropStates.has(key)) cropStates.set(key, { progress: 0 });
  }

  function unregisterAt(x, y, z) {
    const key = blockKey(x, y, z);
    cropStates.delete(key);
    farmlandStates.delete(key);
  }

  function hasNearbyWaterSlow(x, y, z, radius = HYDRATION_RADIUS) {
    const r = Math.max(1, Math.floor(radius));
    const rSq = r * r;
    for (let bz = z - r; bz <= z + r; bz++) {
      for (let by = y - HYDRATION_VERTICAL_RANGE; by <= y + HYDRATION_VERTICAL_RANGE; by++) {
        for (let bx = x - r; bx <= x + r; bx++) {
          const dx = bx - x;
          const dz = bz - z;
          if ((dx * dx) + (dz * dz) > rSq) continue;
          if (world.getBlockIdAt(bx, by, bz) === WATER_ID) return true;
        }
      }
    }
    return false;
  }

  function invalidateHydrationCacheAround(x, y, z, radius = HYDRATION_RADIUS) {
    const cx = Math.round(x);
    const cy = Math.round(y);
    const cz = Math.round(z);
    const r = Math.max(1, Math.floor(radius));
    const rSq = r * r;
    for (let bz = cz - r; bz <= cz + r; bz++) {
      for (let by = cy - HYDRATION_VERTICAL_RANGE; by <= cy + HYDRATION_VERTICAL_RANGE; by++) {
        for (let bx = cx - r; bx <= cx + r; bx++) {
          const dx = bx - cx;
          const dz = bz - cz;
          if ((dx * dx) + (dz * dz) > rSq) continue;
          hydrationCache.delete(blockKey(bx, by, bz));
        }
      }
    }
  }

  function hasNearbyWater(x, y, z, radius = HYDRATION_RADIUS) {
    const normalizedRadius = Math.max(1, Math.floor(radius));
    const key = blockKey(x, y, z);
    const cached = hydrationCache.get(key);
    if (cached && cached.radius === normalizedRadius && cached.expiresAt > hydrationClock) {
      return cached.hydrated;
    }

    const hydrated = hasNearbyWaterSlow(x, y, z, normalizedRadius);
    hydrationCache.set(key, {
      radius: normalizedRadius,
      hydrated,
      expiresAt: hydrationClock + HYDRATION_CACHE_TTL
    });
    return hydrated;
  }

  function replaceBlockState(x, y, z, nextId) {
    const old = world.getBlockAt(x, y, z);
    if (old) world.removeBlock(old, { trackModification: true });
    return world.createBlock(x, y, z, nextId);
  }

  function dropCropLoot(x, y, z, stageId) {
    const stage = Number(stageId);
    if (stage >= CROP_STAGE_MAX) {
      spawnItemDrop(x, y, z, 'wheat', 1 + (Math.random() < 0.35 ? 1 : 0));
      spawnItemDrop(x, y, z, 'wheat_seed', 1 + Math.floor(Math.random() * 2));
      return;
    }
    if (stage >= 52 && Math.random() < 0.75) {
      spawnItemDrop(x, y, z, 'wheat_seed', 1);
      return;
    }
    if (Math.random() < 0.4) spawnItemDrop(x, y, z, 'wheat_seed', 1);
  }

  function tryTillBlock(block) {
    if (!block) return false;
    const blockId = Number(block.userData?.id);
    if (!TILLABLE.has(blockId)) return false;

    const x = Math.round(block.position.x);
    const y = Math.round(block.position.y);
    const z = Math.round(block.position.z);
    if (world.getBlockIdAt(x, y + 1, z) !== null) return false;

    world.removeBlock(block, { trackModification: true });
    const farmland = world.createBlock(x, y, z, FARMLAND_ID);
    if (!farmland) {
      world.createBlock(x, y, z, blockId);
      return false;
    }
    registerFarmland(x, y, z);
    if (typeof onUseHoe === 'function') onUseHoe();
    return true;
  }

  function tryPlantSeed(block) {
    if (!block || Number(block.userData?.id) !== FARMLAND_ID) return false;
    if (ui.getActiveItemId() !== 'wheat_seed' || ui.getActiveItemCount() <= 0) return false;

    const x = Math.round(block.position.x);
    const y = Math.round(block.position.y);
    const z = Math.round(block.position.z);
    if (world.getBlockIdAt(x, y + 1, z) !== null) return false;

    const crop = world.createBlock(x, y + 1, z, CROP_STAGE_MIN);
    if (!crop) return false;
    if (!ui.consumeActiveItem(1)) {
      world.removeBlock(crop, { trackModification: true });
      return false;
    }
    registerFarmland(x, y, z);
    registerCrop(x, y + 1, z);
    return true;
  }

  function tryHarvestMatureCrop(block) {
    if (!block || Number(block.userData?.id) !== CROP_STAGE_MAX) return false;

    const x = Math.round(block.position.x);
    const y = Math.round(block.position.y);
    const z = Math.round(block.position.z);
    const below = world.getBlockIdAt(x, y - 1, z);
    if (below !== FARMLAND_ID) return false;

    world.removeBlock(block, { trackModification: true });
    world.createBlock(x, y, z, CROP_STAGE_MIN);
    registerCrop(x, y, z);
    dropCropLoot(x, y, z, CROP_STAGE_MAX);
    return true;
  }

  function tryUseBoneMeal(block) {
    if (!block) return false;
    if (ui.getActiveItemId() !== 'bone_meal' || ui.getActiveItemCount() <= 0) return false;
    const x = Math.round(block.position.x);
    const y = Math.round(block.position.y);
    const z = Math.round(block.position.z);
    const directId = world.getBlockIdAt(x, y, z);
    const upperId = world.getBlockIdAt(x, y + 1, z);

    const saplingTargetY = directId === SAPLING_ID
      ? y
      : (upperId === SAPLING_ID ? y + 1 : null);
    if (saplingTargetY !== null) {
      if (typeof world.growSaplingAt !== 'function') return false;
      const grown = !!world.growSaplingAt(x, saplingTargetY, z);
      if (!grown) return false;
      return ui.consumeActiveItem(1);
    }

    let cropX = x;
    let cropY = y;
    let cropZ = z;
    let cropId = Number(directId);
    if (!isCropBlockId(cropId) && isCropBlockId(upperId)) {
      cropY = y + 1;
      cropId = Number(upperId);
    }

    if (!isCropBlockId(cropId) || cropId >= CROP_STAGE_MAX) return false;
    if (world.getBlockIdAt(cropX, cropY - 1, cropZ) !== FARMLAND_ID) return false;

    if (!ui.consumeActiveItem(1)) return false;
    const growBy = 1 + (Math.random() < 0.55 ? 1 : 0);
    const nextStage = Math.min(CROP_STAGE_MAX, cropId + growBy);
    replaceBlockState(cropX, cropY, cropZ, nextStage);
    registerCrop(cropX, cropY, cropZ);
    return true;
  }

  function tryUseOnBlock(block) {
    if (!block) return false;
    const activeId = ui.getActiveItemId();
    const activeDef = itemDefs[activeId];
    if (activeDef?.kind === 'tool' && activeDef.toolType === 'hoe' && ui.getActiveItemCount() > 0) {
      if (tryTillBlock(block)) return true;
    }
    if (tryUseBoneMeal(block)) return true;
    if (tryPlantSeed(block)) return true;
    if (tryHarvestMatureCrop(block)) return true;
    return false;
  }

  function handleCropBreakByMining(block) {
    if (!block || !isCropBlockId(block.userData?.id)) return false;
    const x = Math.round(block.position.x);
    const y = Math.round(block.position.y);
    const z = Math.round(block.position.z);
    dropCropLoot(x, y, z, block.userData.id);
    cropStates.delete(blockKey(x, y, z));
    return true;
  }

  function onBlockPlaced(x, y, z, blockId) {
    const id = Number(blockId);
    invalidateHydrationCacheAround(x, y, z);
    if (id === FARMLAND_ID) registerFarmland(x, y, z);
    if (isCropBlockId(id)) registerCrop(x, y, z);
  }

  function onBlockRemoved(x, y, z, blockId = null) {
    const removedId = Number(blockId ?? world.getBlockIdAt(x, y, z));
    invalidateHydrationCacheAround(x, y, z);
    unregisterAt(x, y, z);

    if (removedId === FARMLAND_ID) {
      const aboveId = world.getBlockIdAt(x, y + 1, z);
      if (!isCropBlockId(aboveId)) return;
      const crop = world.getBlockAt(x, y + 1, z);
      if (crop) world.removeBlock(crop, { trackModification: true });
      dropCropLoot(x, y + 1, z, aboveId);
      cropStates.delete(blockKey(x, y + 1, z));
    }
  }

  function updateFarmland(delta) {
    farmlandTickTimer += Math.max(0, delta);
    if (farmlandTickTimer < 1.2) return;

    const tick = farmlandTickTimer;
    farmlandTickTimer = 0;

    for (const [key, state] of farmlandStates.entries()) {
      const { x, y, z } = parseBlockKey(key);
      if (world.getBlockIdAt(x, y, z) !== FARMLAND_ID) {
        farmlandStates.delete(key);
        continue;
      }

      const hydrated = hasNearbyWater(x, y, z, 4);
      const hasCrop = isCropBlockId(world.getBlockIdAt(x, y + 1, z));
      if (hydrated || hasCrop) {
        state.dryTime = 0;
        continue;
      }

      state.dryTime += tick;
      if (state.dryTime < 35) continue;
      replaceBlockState(x, y, z, 2);
      farmlandStates.delete(key);
    }
  }

  function updateCropGrowth(delta) {
    cropTickTimer += Math.max(0, delta);
    if (cropTickTimer < 0.8) return;

    const tick = cropTickTimer;
    cropTickTimer = 0;

    for (const [key, state] of cropStates.entries()) {
      const { x, y, z } = parseBlockKey(key);
      const id = Number(world.getBlockIdAt(x, y, z));
      if (!isCropBlockId(id)) {
        cropStates.delete(key);
        continue;
      }

      if (world.getBlockIdAt(x, y - 1, z) !== FARMLAND_ID) {
        const crop = world.getBlockAt(x, y, z);
        if (crop) world.removeBlock(crop, { trackModification: true });
        dropCropLoot(x, y, z, id);
        cropStates.delete(key);
        continue;
      }

      if (id >= CROP_STAGE_MAX) continue;
      const growthRate = hasNearbyWater(x, y - 1, z, 4) ? 1.0 : 0.55;
      state.progress += tick * growthRate * (0.85 + Math.random() * 0.3);

      const threshold = getGrowthThreshold(id);
      if (state.progress < threshold) continue;

      const nextStage = Math.min(CROP_STAGE_MAX, id + 1);
      const next = replaceBlockState(x, y, z, nextStage);
      if (!next) continue;
      state.progress = 0;
    }
  }

  function update(delta) {
    hydrationClock += Math.max(0, Number(delta) || 0);
    updateFarmland(delta);
    updateCropGrowth(delta);
  }

  function exportState() {
    const farmland = [];
    for (const [key, state] of farmlandStates.entries()) {
      farmland.push({
        key,
        dryTime: Number(state?.dryTime) || 0
      });
    }

    const crops = [];
    for (const [key, state] of cropStates.entries()) {
      crops.push({
        key,
        progress: Number(state?.progress) || 0
      });
    }

    return { farmland, crops };
  }

  function importState(snapshot = null) {
    farmlandStates.clear();
    cropStates.clear();
    hydrationCache.clear();

    const farmland = Array.isArray(snapshot?.farmland) ? snapshot.farmland : [];
    for (const entry of farmland) {
      const key = String(entry?.key || '');
      if (!key) continue;
      farmlandStates.set(key, {
        dryTime: Math.max(0, Number(entry?.dryTime) || 0)
      });
    }

    const crops = Array.isArray(snapshot?.crops) ? snapshot.crops : [];
    for (const entry of crops) {
      const key = String(entry?.key || '');
      if (!key) continue;
      cropStates.set(key, {
        progress: Math.max(0, Number(entry?.progress) || 0)
      });
    }

    cropTickTimer = 0;
    farmlandTickTimer = 0;
    hydrationClock = 0;
  }

  return {
    isCropBlockId,
    tryUseOnBlock,
    handleCropBreakByMining,
    onBlockPlaced,
    onBlockRemoved,
    update,
    exportState,
    importState
  };
}
